<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import LeadsAPI from 'dashboard/api/leads';

import ContactConversations from './ContactConversations.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import ConversationAcquisition from './ConversationAcquisition.vue';
import ConversationLabels from './labels/LabelBox.vue';
import VisitHistory from './VisitHistory.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import PanelSection from './leadPanel/PanelSection.vue';
import LeadIdentity from './leadPanel/LeadIdentity.vue';
import LeadCard from './leadPanel/LeadCard.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { updateUISettings, isContactSidebarItemOpen, toggleSidebarUIState } =
  useUISettings();

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const hasAcquisitionData = computed(() => {
  const attrs = conversationAdditionalAttributes.value;
  return !!(attrs.source || attrs.medium || attrs.landing_page_url);
});

// The conversation's captured lead, fetched directly so the Leads page
// listing state is never clobbered.
const lead = ref(null);
const fetchLead = async () => {
  lead.value = null;
  if (!conversationId.value) return;
  try {
    const { data } = await LeadsAPI.get({
      conversationId: conversationId.value,
    });
    lead.value = (data.payload || [])[0] || null;
  } catch {
    lead.value = null;
  }
};

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

watch(conversationId, fetchLead, { immediate: true });

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

// Secondary sections, collapsed by default; open state persists per user.
const secondarySections = computed(() => [
  { name: 'notes', uiKey: 'is_contact_notes_open', title: 'NOTES' },
  { name: 'previous', uiKey: 'is_previous_conv_open', title: 'PREVIOUS' },
  { name: 'attachments', uiKey: 'is_shared_files_open', title: 'ATTACHMENTS' },
  {
    name: 'attributes',
    uiKey: 'is_contact_attributes_open',
    title: 'ATTRIBUTES',
  },
  { name: 'device', uiKey: 'is_conv_details_open', title: 'DEVICE' },
]);

onMounted(() => {
  getContactDetails();
  fetchLead();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION_SIDEBAR.LEAD_PANEL.TITLE')"
      @close="closeContactPanel"
    />
    <LeadIdentity :contact="contact" />
    <PanelSection :title="$t('CONVERSATION_SIDEBAR.LEAD_PANEL.SECTIONS.LEAD')">
      <LeadCard :lead="lead" />
    </PanelSection>
    <PanelSection
      v-if="hasAcquisitionData"
      :title="$t('CONVERSATION_SIDEBAR.LEAD_PANEL.SECTIONS.ACQUISITION')"
    >
      <ConversationAcquisition
        :conversation-attributes="conversationAdditionalAttributes"
      />
    </PanelSection>
    <PanelSection
      :title="$t('CONVERSATION_SIDEBAR.LEAD_PANEL.SECTIONS.JOURNEY')"
    >
      <VisitHistory :contact-attributes="contactAdditionalAttributes" />
    </PanelSection>
    <PanelSection
      :title="$t('CONVERSATION_SIDEBAR.LEAD_PANEL.SECTIONS.LABELS')"
    >
      <ConversationLabels :conversation-id="conversationId" />
    </PanelSection>
    <template v-for="section in secondarySections" :key="section.name">
      <PanelSection
        :title="$t(`CONVERSATION_SIDEBAR.LEAD_PANEL.SECTIONS.${section.title}`)"
        collapsible
        :is-open="isContactSidebarItemOpen(section.uiKey)"
        @toggle="value => toggleSidebarUIState(section.uiKey, value)"
      >
        <ContactNotes v-if="section.name === 'notes'" :contact-id="contactId" />
        <ContactConversations
          v-else-if="section.name === 'previous' && contact.id"
          :contact-id="contact.id"
          :conversation-id="conversationId"
        />
        <SharedFiles v-else-if="section.name === 'attachments'" />
        <CustomAttributes
          v-else-if="section.name === 'attributes'"
          attribute-type="contact_attribute"
          attribute-from="conversation_contact_panel"
          :contact-id="contact.id"
          :empty-state-message="
            $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
          "
        />
        <ConversationInfo
          v-else-if="section.name === 'device'"
          :conversation-attributes="conversationAdditionalAttributes"
          :contact-attributes="contactAdditionalAttributes"
        />
      </PanelSection>
    </template>
    <PanelSection
      v-if="isLinearFeatureEnabled && isLinearClientIdConfigured"
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
      collapsible
      :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
      @toggle="value => toggleSidebarUIState('is_linear_issues_open', value)"
    >
      <LinearSetupCTA v-if="!isLinearConnected" />
      <LinearIssuesList v-else :conversation-id="conversationId" />
    </PanelSection>
    <PanelSection
      v-if="isShopifyFeatureEnabled"
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
      collapsible
      :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
      @toggle="value => toggleSidebarUIState('is_shopify_orders_open', value)"
    >
      <ShopifyOrdersList :contact-id="contactId" />
    </PanelSection>
    <div class="pb-8" />
  </div>
</template>
